/** The app: who's signed in decides between signing in and the screens below. */

import { HomeIcon, ListIcon, UserIcon } from "@taxi/mobile-web/icons";
import { Link, match, navigate, useLocation } from "@taxi/mobile-web/router";
import { Button, Loading, ToastProvider } from "@taxi/mobile-web/ui";
import { type ReactNode, useEffect } from "react";
import { DraftProvider } from "./lib/draft";
import { SessionProvider, useSession } from "./lib/session";
import { AccountScreen } from "./screens/account";
import { AccountRequestScreen } from "./screens/account-request";
import { BookingScreen } from "./screens/booking";
import { BookingsScreen } from "./screens/bookings";
import { CardsScreen } from "./screens/cards";
import { ConfirmScreen } from "./screens/confirm";
import { DetailsScreen } from "./screens/details";
import { HomeScreen } from "./screens/home";
import { PayScreen } from "./screens/pay";
import { PlacesScreen } from "./screens/places";
import { QuotesScreen } from "./screens/quotes";
import { ReceiptScreen } from "./screens/receipt";
import { SignInScreen } from "./screens/sign-in";
import { WhereScreen } from "./screens/where";

type Route = { path: string; render: (params: Record<string, string>) => ReactNode; nav?: boolean };

const ROUTES: Route[] = [
  { path: "/", render: () => <HomeScreen />, nav: true },
  { path: "/book", render: () => <WhereScreen /> },
  { path: "/book/quotes", render: () => <QuotesScreen /> },
  { path: "/book/confirm", render: () => <ConfirmScreen /> },
  { path: "/bookings", render: () => <BookingsScreen />, nav: true },
  { path: "/bookings/:id", render: ({ id }) => <BookingScreen key={id} id={id!} /> },
  { path: "/bookings/:id/pay", render: ({ id }) => <PayScreen key={id} id={id!} /> },
  { path: "/bookings/:id/receipt", render: ({ id }) => <ReceiptScreen id={id!} /> },
  { path: "/account", render: () => <AccountScreen />, nav: true },
  { path: "/account/details", render: () => <DetailsScreen /> },
  { path: "/account/places", render: () => <PlacesScreen /> },
  { path: "/account/cards", render: () => <CardsScreen /> },
  { path: "/account/pay-on-account", render: () => <AccountRequestScreen /> },
];

function BottomNav({ path }: { path: string }) {
  const tabs = [
    { to: "/", label: "Book", icon: <HomeIcon />, active: path === "/" },
    {
      to: "/bookings",
      label: "Bookings",
      icon: <ListIcon />,
      active: path.startsWith("/bookings"),
    },
    { to: "/account", label: "Account", icon: <UserIcon />, active: path.startsWith("/account") },
  ];
  return (
    <nav className="bottom-nav" aria-label="Main">
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          replace
          className={tab.active ? "active" : ""}
          aria-current={tab.active ? "page" : undefined}
        >
          {tab.icon}
          <span>{tab.label}</span>
        </Link>
      ))}
    </nav>
  );
}

function Screens() {
  const { me } = useSession();
  const { path, query } = useLocation();

  // Signed out: everything waits behind signing in, then carries on where it was going.
  const next = query.get("next");
  useEffect(() => {
    if (!me && path !== "/sign-in") {
      const here = `${path}${query.size ? `?${query}` : ""}`;
      navigate(here === "/" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(here)}`, {
        replace: true,
      });
    } else if (me && path === "/sign-in") {
      // Only somewhere in this app.
      const inApp = next?.startsWith("/") && !next.startsWith("//") && !next.includes("\\");
      navigate(inApp ? next! : "/", { replace: true });
    }
  }, [me, path, query, next]);

  if (!me) return path === "/sign-in" ? <SignInScreen /> : <Loading />;
  if (path === "/sign-in") return <Loading />;
  for (const route of ROUTES) {
    const params = match(route.path, path);
    if (params) {
      return (
        <>
          <main className={route.nav ? "screen has-nav" : "screen"}>{route.render(params)}</main>
          {route.nav && <BottomNav path={path} />}
        </>
      );
    }
  }
  return (
    <main className="screen">
      <div className="empty">
        <h2>Page not found</h2>
        <Button onClick={() => navigate("/", { replace: true })}>Go to the start</Button>
      </div>
    </main>
  );
}

export function App() {
  return (
    <ToastProvider>
      <SessionProvider
        loading={<Loading label="Starting up" />}
        failed={(retry) => (
          <main className="screen">
            <div className="empty">
              <h2>We can't connect right now</h2>
              <p>Check your connection and try again.</p>
              <Button onClick={retry}>Try again</Button>
            </div>
          </main>
        )}
      >
        <DraftProvider>
          <Screens />
        </DraftProvider>
      </SessionProvider>
    </ToastProvider>
  );
}
